/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { get as _get, cloneDeep as _cloneDeep } from 'lodash';

import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

import DataContext from '../../../../data/data-context';
import Auth, { ProfileContext } from '../../../../data/auth-service';
import AwsService, { scaleGroupFields } from '../../../../data/aws-service';
import TargetService, {
    fields as targetFields,
} from '../../../../data/target-service';
import SessionService, {
    sessionFields,
} from '../../../../data/session-service';
import FilterService, { filterFields } from '../../../../data/filter-service';
import SystemService from '../../../../data/system-service';
import SensorsService from '../../../../data/sensors-service';

import ConfirmationDialog from '../../../../components/confirmationdlg';
import SearchBar from '../../../../components/searchbar';
import {
    LoadingOverlay,
    NoRowsOverlay,
} from '../../../../components/xgrid/overlays';

import useInterval from '../../../../libraries/useinterval';
import useWindowSize from '../../../../libraries/usewindowsize';

const useStyles = makeStyles((theme) => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
}));

const AWS = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [awsTab, setAwsTab] = useState(0);
    const [filterData, setFilterData] = useState([]);
    const [targetData, setTargetData] = useState([]);
    const [sessionData, setSessionData] = useState([]);
    const [deleteScaleGroupDialog, setDeleteScaleGroupDialog] = useState(false);
    const [deleteFilterDialog, setDeleteFilterDialog] = useState(false);
    const [deleteTargetDialog, setDeleteTargetDialog] = useState(false);
    const [deleteSessionDialog, setDeleteSessionDialog] = useState(false);
    const [selectedScaleGroup, setSelectedScaleGroup] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const awsPerms = Auth.permissions.get('AWS');
    const [readonly] = useState(!awsPerms.write);
    const groupAPIRef = useApiRef();
    const filterAPIRef = useApiRef();
    const targetAPIRef = useApiRef();
    const sessionAPIRef = useApiRef();
    const { height } = useWindowSize();
    const [columnWidth, setColumnWidth] = useState({});
    const [columns, setColumns] = useState([]);
    const [row, setRows] = useState([]);
    const [filterService, setFilterService] = useState(null);
    const [targetService, setTargetService] = useState(null);
    const [sessionService, setSessionService] = useState(null);
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [loading, setLoading] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [filterColumnWidth, setFilterColumnWidth] = useState({});
    const [targetColumnWidth, setTargetColumnWidth] = useState({});
    const [sessionColumnWidth, setSessionColumnWidth] = useState({});
    const [valid, setValid] = useState(false);
    const [sensors, setSensors] = useState([]);
    const [applianceFilter] = useState({
        sensor: true,
        btsensor: false,
    });
    const [filter] = useState({
        q: '',
        limit: 1000,
        name: '',
        host: '',
        model: '',
        status: {
            critical: true,
            warning: true,
            normal: true,
        },
        policies: [],
        sort: 'status+desc',
    });

    const getSensors = (search, applianceFilter) => {
        SensorsService.getData(search).then((res) => {
            if (!res) res = [];
            setSensors(applyApplianceFilter(res, applianceFilter));
        });
    };

    const applyApplianceFilter = (res, appFil) => {
        const s = res.filter((v) => {
            const bt = _get(v, 'data.backtesting', '');
            if (appFil.sensor && !bt) {
                return v;
            } else if (appFil.btsensor && bt) {
                return v;
            } else if (!appFil.sensor && !appFil.btsensor) {
                return v;
            }
            return null;
        });

        return s;
    };

    const buildFilter = useCallback(() => {
        const f = Object.keys(filter).reduce((a, c) => {
            switch (c) {
                case 'status':
                    a = Array.prototype.concat(
                        a,
                        Object.keys(filter[c]).map((v) => {
                            if (filter[c][v]) return 'status=' + encodeURIComponent(v);
                            return null;
                        })
                    );
                    break;
                case 'sort':
                    a.push('sort=' + filter[c]);
                    break;
                case 'policies':
                    a = Array.prototype.concat(
                        a,
                        filter[c].map((v) => {
                            return 'filter=' + encodeURIComponent(`${v.type}=${v.name}`);
                        })
                    );
                    break;
                case 'q':
                    if (filter[c]) { a.push('q=' + encodeURIComponent(filter[c].toLowerCase())) }
                    break;
                case 'limit':
                    if (filter[c]) a.push('limit=' + filter[c]);
                    break;
                default:
                    if (filter[c]) { a.push('filter=' + encodeURIComponent(`${c}=${filter[c]}`)) }
            }

            return a;
        }, []);

        return f.join('&');
    }, [filter]);

    useEffect(() => {
        getSensors(buildFilter(), applianceFilter);
    }, []);

    useEffect(() => {
        getSensors(buildFilter(), applianceFilter);
    }, [applianceFilter]);

    useInterval(() => {
        getSensors(buildFilter(), applianceFilter);
    }, 15000);

    useEffect(() => {
        if (location.state && location.state.tab) {
            setAwsTab(location.state.tab);
        }
        setFilterColumnWidth(getProfile('filterData').filterWidths || {});
        setTargetColumnWidth(getProfile('targetData').targetWidths || {});
        setSessionColumnWidth(getProfile('sessionData').sessionWidths || {});
    }, [location.state]);

    const handleColumnResize = (params) => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('aws', {
            ...getProfile('aws'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleFilterColumnResize = (params) => {
        const widths = filterAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        setFilterColumnWidth(widths);
    };

    useEffect(() => {
        if (systemSettings && systemSettings.aws && enabled) {
            const a = _cloneDeep(systemSettings.aws);
            if (!a.as_conf.auto_scale_groups) {
                a.as_conf.auto_scale_groups = [];
            }
            const d = a.as_conf.auto_scale_groups.map((v, uid) => ({
                ...v,
                id: uid,
            }));
            setRows(d);
        } 
        setColumnWidth(getProfile('aws').widths || {});
    }, [systemSettings, updateSystemSettings,enabled]);

    const handleTargetColumnResize = (params) => {
        const widths = targetAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        setTargetColumnWidth(widths);
    };

    const handleSessionColumnResize = (params) => {
        const widths = sessionAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        setSessionColumnWidth(widths);
    };

    const handleFilterColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = filterAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);
        });
    };

    const handleTargetColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = targetAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);
        });
    };

    const handleSessionColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = sessionAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);
        });
    };

    const tableRenderers = {
        name: (d) => <div>{d.row.name}</div>,
        gre_remote: (d) => <div>{d.row.gre_remote}</div>,
        metric_type: (d) => <div>{d.row.group_scaling_policy.metric_type}</div>,
        gre_key: (d) => {
            <div>{d.row.gre_key}</div>;
        },
    };

    const filtertableRenderers = {
        Description: ({ row }) => <div>{row.Description}</div>,
        NetworkService: ({ row }) => <div>{row.NetworkService}</div>,
        IngressFilterRules: ({ row }) => (
            <div>{row.IngressFilterRules.length > 0 ? 'Yes' : 'No'}</div>
        ),
        EgressFilterRules: ({ row }) => (
            <div>{row.EgressFilterRules.length > 0 ? 'Yes' : 'No'}</div>
        ),
    };

    const targettableRenderers = {
        Description: ({ row }) => <div>{row.Description}</div>,
        Type: ({ row }) => <div>{row.Type}</div>,
        Target: ({ row }) => <div>{row.NetworkInterfaceId}</div>,
    };

    const sessiontableRenderers = {
        Description: ({ row }) => <div>{row.Description}</div>,
        Source: ({ row }) => <div>{row.NetworkInterfaceId}</div>,
        Target: ({ row }) => <div>{row.TrafficMirrorTargetId}</div>,
        Filter: ({ row }) => <div>{row.TrafficMirrorFilterId}</div>,
        VNI: ({ row }) => <div>{row.VirtualNetworkId}</div>,
    };

    const generateColumns = useCallback(() => {
        const p = getProfile('aws');
        let c = scaleGroupFields.map((f) => ({
            ...f,
            width: columnWidth[f.headerName] || 0,
            ...(!columnWidth[f.headerName] ? { flex: 1 } : {}),
            field: f.field,
            disableColumnMenu: true,
            renderCell: tableRenderers[f.variable],
        }));

        if (p.columns && p.columns.length) {
            c = p.columns.map((v) => {
                for (let i = c.length; i--;) {
                    if (v === c[i].headerName) {
                        return c[i];
                    }
                }
                return null;
            });
        }
        setColumns(c);
    }, [columnWidth, getProfile]);

    useEffect(() => {
        generateColumns();
    }, [generateColumns]);

    const filterColumns = filterFields.map((f) => {
        return {
            ...f,
            sortable: true,
            width: filterColumnWidth[f.title] || 0,
            ...(!filterColumnWidth[f.title] ? { flex: 1 } : {}),
            headerName: f.title,
            valueGetter: (p) => {
                return p.row[f.variable];
            },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: filtertableRenderers[f.variable],
        };
    });

    const targetColumns = targetFields.map((t) => {
        return {
            ...t,
            sortable: true,
            width: targetColumnWidth[t.title] || 0,
            ...(!targetColumnWidth[t.title] ? { flex: 1 } : {}),
            headerName: t.title,
            valueGetter: (p) => {
                return p.row[t.variable];
            },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: targettableRenderers[t.variable],
        };
    });

    const sessionColumns = sessionFields.map((s) => {
        return {
            ...s,
            sortable: true,
            width: sessionColumnWidth[s.title] || 0,
            ...(!sessionColumnWidth[s.title] ? { flex: 1 } : {}),
            headerName: s.title,
            valueGetter: (p) => {
                return p.row[s.variable];
            },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: sessiontableRenderers[s.variable],
        };
    });

    const handleSelectAutoScaleGroup = (s) => {
        setSelectedScaleGroup(s.row);
    };

    const handleSelectFilter = (s) => {
        setSelectedFilter(s.row);
    };

    const handleSelectTarget = (s) => {
        setSelectedTarget(s.row);
    };

    const handleSelectSession = (s) => {
        setSelectedSession(s.row);
    };

    const handleCreateGroup = () => {
        navigate('/system/port_mirroring/aws/create_group');
    };

    const handleCreateFilter = () => {
        navigate('/system/port_mirroring/aws/create_filter');
    };

    const handleCreateTarget = () => {
        navigate('/system/port_mirroring/aws/create_target');
    };

    const handleCreateSession = () => {
        navigate('/system/port_mirroring/aws/create_session');
    };

    const handleEditAutoScaleGroup = (e) => {
        navigate(`/system/port_mirroring/aws/update_group/${selectedScaleGroup.id}`);
    };

    const handleEditFilter = () => {
        navigate(
            `/system/port_mirroring/aws/update_filter/${selectedFilter.TrafficMirrorFilterId}`
        );
    };

    const handleEditSession = () => {
        navigate(
            `/system/port_mirroring/aws/update_session/${selectedSession.TrafficMirrorSessionId}`
        );
    };

    const handleConfigureFilter = () => {
        navigate(
            `/system/port_mirroring/aws/configure_filter_rule/${selectedFilter.TrafficMirrorFilterId}`
        );
    };

    const handleAwsConfig = (e) => {
        navigate('/system/port_mirroring/aws/aws_credentials');
    };

    const handleCloseDeleteScaleGroup = () => {
        setDeleteScaleGroupDialog(false);
        setTimeout(() => {
            setSelectedScaleGroup(null);
        }, 1000);
    };

    const handleCloseDeleteFilterDialog = () => {
        setDeleteFilterDialog(false);
        setTimeout(() => {
            setSelectedFilter(null);
        }, 1000);
    };

    const handleCloseDeleteTargetDialog = () => {
        setDeleteTargetDialog(false);
        setTimeout(() => {
            setSelectedTarget(null);
        }, 1000);
    };

    const handleCloseDeleteSessionDialog = () => {
        setDeleteSessionDialog(false);
        setTimeout(() => {
            setSelectedSession(null);
        }, 1000);
    };

    const handleOpenDeleteScaleGroup = () => {
        setDeleteScaleGroupDialog(true);
    };

    const handleOpenDeleteFilter = () => {
        setDeleteFilterDialog(true);
    };

    const handleOpenDeleteTarget = () => {
        setDeleteTargetDialog(true);
    };

    const handleOpenDeleteSession = () => {
        setDeleteSessionDialog(true);
    };

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            if (!readonly) {
                if (systemSettings.aws.as_conf.enabled) {
                    SystemService.awsCredAccess().then((res) => {
                        if (res) {
                            const region = systemSettings.aws.as_conf.region;
                            const accessKeyId = systemSettings.aws.user_creds.access_key_id;
                            const secretAccessKey = res.AWSSK;
                            if (region?.trim() && accessKeyId?.trim() && secretAccessKey?.trim()) {
                                setValid(true);
                                initializeService(region, accessKeyId, secretAccessKey);
                            }
                        }
                    });
                }
                setEnabled(systemSettings.aws.as_conf.enabled);
            }
        }
    }, [systemSettings]);

    const initializeService = async (awsregion, accessKeyId, secretAccessKey) => {
        const config = {
            region: awsregion,
            credentials: {
                accessKeyId,
                secretAccessKey,
            },
        };
        const targetservice = new TargetService(config);
        const sessionservice = new SessionService(config);
        const filterservice = new FilterService(config);
        setTargetService(targetservice);
        setSessionService(sessionservice);
        setFilterService(filterservice);
    };

    const getFilters = async (service) => {
        setLoading(true);
        if (valid) {
            try {
                const filters = await service.getFilters();
                setFilterData(filters || []);
            } catch (error) {
                console.error('Error fetching filters:', error);
            } finally {
                setLoading(false);
            }
        }

    };

    const getTargets = async (service) => {
        setLoading(true);
        if (valid) {
            try {
                const targets = await service.getTargets();
                setTargetData(targets || []);
            } catch (error) {
                console.error('Error fetching targets:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const getSessions = async (service) => {
        setLoading(true);
        if (valid) {
            try {
                const sessions = await service.getSessions();
                setSessionData(sessions || []);
            } catch (error) {
                console.error('Error fetching sessions:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteAutoScaleGroup = () => {
        const a = _cloneDeep(systemSettings);
        a.aws.as_conf.auto_scale_groups.splice(selectedScaleGroup.id, 1);
        updateSystemSettings(a, 'Deleted Auto Scale Group');
        setSelectedScaleGroup(null);
        setDeleteScaleGroupDialog(false);
    };

    const handleDeleteFilter = async () => {
        try {
            const updatedfilters = [...filterData];
            const index = updatedfilters.findIndex(
                (item) =>
                    item.TrafficMirrorFilterId === selectedFilter.TrafficMirrorFilterId
            );
            const res = await filterService.deleteFilter(
                selectedFilter.TrafficMirrorFilterId
            );
            if (res.$metadata.httpStatusCode === 200) {
                updatedfilters.splice(index, 1);
                setFilterData(updatedfilters);
                setSelectedFilter(null);
                setDeleteFilterDialog(false);
                SystemService.addAuditRecord({ source: 'AWS config', subject: selectedFilter.Description, action: 'remove mirror filter', result: true });
                toast.success('Filter deleted successfully');
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setDeleteFilterDialog(false);
            SystemService.addAuditRecord({ source: 'AWS config', subject: selectedFilter.Description, action: 'Error: ' + error, result: false });
            toast.error(`Failed to delete Filter: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTarget = async () => {
        try {
            const updateTargetData = [...targetData];
            const index = updateTargetData.findIndex(
                (item) =>
                    item.TrafficMirrorTargetId === selectedTarget.TrafficMirrorTargetId
            );
            const res = await targetService.deleteTarget(
                selectedTarget.TrafficMirrorTargetId
            );
            if (res.$metadata.httpStatusCode === 200) {
                updateTargetData.splice(index, 1);
                setTargetData(updateTargetData);
                SystemService.addAuditRecord({ source: 'AWS config', subject: selectedTarget.Description, action: 'remove mirror target', result: true });
                toast.success('Target deleted successfully');
                setSelectedTarget(null);
                setDeleteTargetDialog(false);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setDeleteTargetDialog(false);
            SystemService.addAuditRecord({ source: 'AWS config', subject: selectedTarget.Description, action: 'Error: ' + error, result: false });
            toast.error(`Failed to delete Target: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSession = async () => {
        try {
            const updateSessionData = [...sessionData];
            const index = updateSessionData.findIndex(
                (item) =>
                    item.TrafficMirrorSessionId === selectedSession.TrafficMirrorSessionId
            );
            const target = await targetService.getTargetById(
                selectedSession.TrafficMirrorTargetId
            );
            const targetDetails = target[0].NetworkInterfaceId;
            const instanceDetails = await targetService.getInstances(targetDetails);
            const instance = instanceDetails.Reservations[0].Instances[0];
            if (instance) {
                const uuids = sensors
                    .map((v) => {
                        if (v.host === instance.PrivateIpAddress) {
                            return v.uuid;
                        }
                        return null;
                    })
                    .filter((uuid) => uuid !== null);
                const uuid = uuids.length > 0 ? uuids[0] : null;
                if (uuid) {
                    SensorsService.getSensor(uuid).then((res) => {
                        const updatedSensorData = {
                            data: {
                                ..._cloneDeep(res.data),
                            },
                            update_vxlan: true,
                        };
                        updatedSensorData.data.vxlan_id = parseInt('');
                        updatedSensorData.data.vxlan_dstport = parseInt('');
                        updatedSensorData.data.vxlan_mtu = parseInt('');
                        SensorsService.updateSensor(uuid, updatedSensorData);
                    });
                }
            }

            const res = await sessionService.deleteSession(
                selectedSession.TrafficMirrorSessionId
            );
            if (res.$metadata.httpStatusCode === 200) {
                updateSessionData.splice(index, 1);
                setSessionData(updateSessionData);
                SystemService.addAuditRecord({ source: 'AWS config', subject: selectedSession.Description, action: 'remove mirror session', result: true });
                toast.success('Session deleted successfully');
                setSelectedSession(null);
                setDeleteSessionDialog(false);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setDeleteSessionDialog(false);
            SystemService.addAuditRecord({ source: 'AWS config', subject: selectedSession.Description, action: 'Error: ' + error, result: false });
            toast.error(`Failed to delete Session: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (e, tab) => {
        setAwsTab(tab);
    };

    useEffect(() => {
        if (enabled && filterService) {
            getFilters(filterService);
        }
    }, [enabled, filterService]);

    useEffect(() => {
        if (enabled && targetService) {
            getTargets(targetService);
        }
    }, [enabled, targetService]);

    useEffect(() => {
        if (enabled && sessionService) {
            getSessions(sessionService);
        }
    }, [enabled, sessionService]);

    const handleFilterGroups = ({ search }) => {
        groupAPIRef.current.setFilterModel({
            items: [
                { operatorValue: 'contains', columnField: 'name', value: search },
            ],
        });
    };

    const handleFilterFilters = ({ search }) => {
        filterAPIRef.current.setFilterModel({
            items: [
                {
                    operatorValue: 'contains',
                    columnField: 'Description',
                    value: search,
                },
            ],
        });
    };

    const handleFilterTargets = ({ search }) => {
        targetAPIRef.current.setFilterModel({
            items: [
                {
                    operatorValue: 'contains',
                    columnField: 'Description',
                    value: search,
                },
            ],
        });
    };

    const handleFilterSessions = ({ search }) => {
        sessionAPIRef.current.setFilterModel({
            items: [
                {
                    operatorValue: 'contains',
                    columnField: 'Description',
                    value: search,
                },
            ],
        });
    };

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item mt={1} xs={12}>
                    {enabled ? (
                        <Box>
                            {(awsPerms.write || awsPerms.admin) && (
                                <Button
                                    variant="contained"
                                    className={classes.buttonRight}
                                    color="secondary"
                                    aria-label="configure"
                                    onClick={handleAwsConfig}
                                >
                                    <EditIcon /> Configure Credentials
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            {(awsPerms.write || awsPerms.admin) && (
                                <Button
                                    variant="contained"
                                    className={classes.buttonRight}
                                    color="primary"
                                    aria-label="configure"
                                    onClick={handleAwsConfig}
                                >
                                    <CloudIcon /> Configure Credentials
                                </Button>
                            )}
                        </Box>
                    )}

                    <Box className={classes.fullWidth} hidden={awsTab !== 0}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar
                                immediate
                                onSearch={handleFilterGroups}
                                searchservice={AwsService}
                            />
                            <Box mr={2}>
                                {!readonly && (
                                    <Button
                                        variant="contained"
                                        className={classes.buttonLeft}
                                        color="primary"
                                        disabled={!enabled}
                                        aria-label="add user"
                                        onClick={handleCreateGroup}
                                    >
                                        <AddIcon /> Create Group
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.fullWidth} hidden={awsTab !== 1}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar
                                immediate
                                onSearch={handleFilterFilters}
                                searchservice={
                                    filterService || { searchPlaceholder: 'Search Mirror Filter' }
                                }
                            />
                            <Box mr={2}>
                                {!readonly && (
                                    <Button
                                        variant="contained"
                                        className={classes.buttonLeft}
                                        color="primary"
                                        aria-label="add filter"
                                        disabled={!enabled}
                                        onClick={handleCreateFilter}
                                    >
                                        <AddIcon /> Create Mirror Filter
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.fullWidth} hidden={awsTab !== 2}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar
                                immediate
                                onSearch={handleFilterTargets}
                                searchservice={
                                    targetService || { searchPlaceholder: 'Search Mirror Target' }
                                }
                            />
                            <Box mr={2}>
                                {!readonly && (
                                    <Button
                                        variant="contained"
                                        className={classes.buttonLeft}
                                        color="primary"
                                        aria-label="add target"
                                        onClick={handleCreateTarget}
                                        disabled={!enabled}
                                    >
                                        <AddIcon /> Create Mirror Target
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={classes.fullWidth} hidden={awsTab !== 3}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar
                                immediate
                                onSearch={handleFilterSessions}
                                searchservice={
                                    sessionService || {
                                        searchPlaceholder: 'Search Mirror Session',
                                    }
                                }
                            />
                            <Box mr={2}>
                                {!readonly && (
                                    <Button
                                        variant="contained"
                                        className={classes.buttonLeft}
                                        color="primary"
                                        aria-label="add session"
                                        onClick={handleCreateSession}
                                        disabled={!enabled}
                                    >
                                        <AddIcon /> Create Mirror Session
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <AppBar position="static" className={classes.tabBar}>
                        <Tabs value={awsTab} onChange={handleTabChange}>
                            <Tab aria-label="Groups" label="Groups" value={0} />
                            <Tab
                                aria-label="Mirror Filters"
                                label="Mirror Filters"
                                value={1}
                            />
                            <Tab
                                aria-label="Mirror Targets"
                                label="Mirror Targets"
                                value={2}
                            />
                            <Tab
                                aria-label="Mirror Sessions"
                                label="Mirror Sessions"
                                value={3}
                            />
                        </Tabs>
                    </AppBar>
                    <Box
                        value={awsTab}
                        index={0}
                        hidden={awsTab !== 0}
                        height={height - 310}
                        width="100%"
                    >
                        <DataGridPro
                            apiRef={groupAPIRef}
                            components={{
                                Header: () => {
                                    return (
                                        <Box
                                            p={1}
                                            display="flex"
                                            justifyContent="flex-end"
                                            borderBottom="1px solid #666666"
                                        >
                                            <IconButton
                                                disabled={!selectedScaleGroup}
                                                variant="contained"
                                                color="primary"
                                                name="edit scale group"
                                                aria-label="edit scale group"
                                                title="edit scale group"
                                                onClick={handleEditAutoScaleGroup}
                                                size="large"
                                            >
                                                {readonly ? <VisibilityIcon /> : <EditIcon />}
                                            </IconButton>
                                            {!readonly && (
                                                <IconButton
                                                    disabled={!selectedScaleGroup}
                                                    variant="contained"
                                                    color="secondary"
                                                    name="delete scale group"
                                                    aria-label="delete scale group"
                                                    title="delete scale group"
                                                    onClick={handleOpenDeleteScaleGroup}
                                                    size="large"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    );
                                },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            rows={row}
                            columns={columns}
                            disableMultipleSelection
                            disableColumnMenu
                            disableVirtualization
                            onRowClick={handleSelectAutoScaleGroup}
                            onColumnResize={handleColumnResize}
                        />
                    </Box>
                    <Box
                        value={awsTab}
                        index={1}
                        hidden={awsTab !== 1}
                        height={height - 310}
                        width="100%"
                    >
                        <DataGridPro
                            apiRef={filterAPIRef}
                            components={{
                                Header: () => {
                                    return (
                                        <Box
                                            p={1}
                                            display="flex"
                                            justifyContent="flex-end"
                                            borderBottom="1px solid #666666"
                                        >
                                            <IconButton
                                                disabled={!selectedFilter}
                                                variant="contained"
                                                color="primary"
                                                name="configure filter"
                                                aria-label="configure filter"
                                                title="configure filter"
                                                onClick={handleConfigureFilter}
                                                size="large"
                                            >
                                                <SettingsIcon />
                                            </IconButton>
                                            <IconButton
                                                disabled={!selectedFilter}
                                                variant="contained"
                                                color="primary"
                                                name="edit filter"
                                                aria-label="edit filter"
                                                title="edit filter"
                                                onClick={handleEditFilter}
                                                size="large"
                                            >
                                                {readonly ? <VisibilityIcon /> : <EditIcon />}
                                            </IconButton>
                                            {!readonly && (
                                                <IconButton
                                                    disabled={!selectedFilter}
                                                    variant="contained"
                                                    color="secondary"
                                                    name="delete filter"
                                                    aria-label="delete filter"
                                                    title="delete filter"
                                                    onClick={handleOpenDeleteFilter}
                                                    size="large"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    );
                                },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            loading={loading}
                            rows={filterData}
                            columns={(() => {
                                const p = getProfile('filtersData');
                                let c = filterColumns;

                                if (p.filterColumns && p.filterColumns.length) {
                                    c = p.filterColumns.map((v) => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return v;
                                    });
                                }
                                return c;
                            })()}
                            onRowClick={handleSelectFilter}
                            disableMultipleSelection
                            disableColumnMenu
                            getRowId={(r) => r.TrafficMirrorFilterId}
                            onColumnResize={handleFilterColumnResize}
                            onColumnOrderChange={handleFilterColumnReorder}
                        />
                    </Box>
                    <Box
                        value={awsTab}
                        index={2}
                        hidden={awsTab !== 2}
                        height={height - 310}
                        width="100%"
                    >
                        <DataGridPro
                            apiRef={targetAPIRef}
                            components={{
                                Header: readonly
                                    ? () => null
                                    : () => {
                                        return (
                                            <Box
                                                p={1}
                                                display="flex"
                                                justifyContent="flex-end"
                                                borderBottom="1px solid #666666"
                                            >
                                                <IconButton
                                                    disabled={!selectedTarget}
                                                    variant="contained"
                                                    color="secondary"
                                                    name="delete target"
                                                    aria-label="delete target"
                                                    title="delete target"
                                                    onClick={handleOpenDeleteTarget}
                                                    size="large"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        );
                                    },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            loading={loading}
                            rows={targetData}
                            columns={(() => {
                                const p = getProfile('targetData');
                                let c = targetColumns;

                                if (p.targetColumns && p.targetColumns.length) {
                                    c = p.targetColumns.map((v) => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return v;
                                    });
                                }
                                return c;
                            })()}
                            onRowClick={handleSelectTarget}
                            disableMultipleSelection
                            disableColumnMenu
                            getRowId={(r) => r.TrafficMirrorTargetId}
                            onColumnResize={handleTargetColumnResize}
                            onColumnOrderChange={handleTargetColumnReorder}
                        />
                    </Box>
                    <Box
                        value={awsTab}
                        index={3}
                        hidden={awsTab !== 3}
                        height={height - 310}
                        width="100%"
                    >
                        <DataGridPro
                            apiRef={sessionAPIRef}
                            components={{
                                Header: () => {
                                    return (
                                        <Box
                                            p={1}
                                            display="flex"
                                            justifyContent="flex-end"
                                            borderBottom="1px solid #666666"
                                        >
                                            <IconButton
                                                disabled={!selectedSession}
                                                variant="contained"
                                                color="primary"
                                                name="edit session"
                                                aria-label="edit session"
                                                title="edit session"
                                                onClick={handleEditSession}
                                                size="large"
                                            >
                                                {readonly ? <VisibilityIcon /> : <EditIcon />}
                                            </IconButton>
                                            {!readonly && (
                                                <IconButton
                                                    disabled={!selectedSession}
                                                    variant="contained"
                                                    color="secondary"
                                                    name="delete session"
                                                    aria-label="delete session"
                                                    title="delete session"
                                                    onClick={handleOpenDeleteSession}
                                                    size="large"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    );
                                },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            loading={loading}
                            rows={sessionData}
                            columns={(() => {
                                const p = getProfile('sessionData');
                                let c = sessionColumns;

                                if (p.sessionColumns && p.sessionColumns.length) {
                                    c = p.sessionColumns.map((v) => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return v;
                                    });
                                }
                                return c;
                            })()}
                            onRowClick={handleSelectSession}
                            disableMultipleSelection
                            disableColumnMenu
                            getRowId={(r) => r.TrafficMirrorSessionId}
                            onColumnResize={handleSessionColumnResize}
                            onColumnOrderChange={handleSessionColumnReorder}
                        />
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteScaleGroup}
                onOK={handleDeleteAutoScaleGroup}
                open={deleteScaleGroupDialog}
                message={`Are you sure you want to remove ${selectedScaleGroup ? selectedScaleGroup.name : ''
                    }?`}
                title="Delete Auto Scale Group"
                cancelLabel="Cancel"
                okLabel="Yes"
            />

            <ConfirmationDialog
                onClose={handleCloseDeleteFilterDialog}
                onOK={handleDeleteFilter}
                open={deleteFilterDialog}
                message={`Are you sure you want to remove the filter ${selectedFilter ? selectedFilter.Description : ''
                    }?`}
                title="Delete Filter"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteTargetDialog}
                onOK={handleDeleteTarget}
                open={deleteTargetDialog}
                message={`Are you sure you want to remove the target ${selectedTarget ? selectedTarget.Description : ''
                    }?`}
                title="Delete Target"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteSessionDialog}
                onOK={handleDeleteSession}
                open={deleteSessionDialog}
                message={`Are you sure you want to remove the session ${selectedSession ? selectedSession.Description : ''
                    }?`}
                title="Delete Session"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </React.Fragment>
    );
};

export default AWS;
